import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/Controls/Input';
import { Textarea } from '@/components/Controls/Textarea';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect, useState } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { Radio } from '@/components/Controls/Radio';
import { CheckPointType } from '@/api/entities/check-point';
import { ErrorMessage } from '@/components/Message/ErrorMessage';
import Modal from '@/components/Modal';
import { showModal } from '@/utils/modal.utils';
import { getTranslatedItem } from '@/utils/language.utils';
import { VideoUpload } from '@/components/FileUpload/VideoUpload';
import {
  useGetMatchingCheckPointError,
  useUploadCheckPointVideo,
} from '@/hooks/check-point.hooks';
import Loader from '@/components/Loading/Loader';
import { setCheckPointRequiredFields } from '@/utils/required-fields/checkPoint.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { FieldError } from '@/type/error.types';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { Checkbox } from '@/components/Controls/Checkbox';
import { Trans } from 'react-i18next';

interface UploadedVideoData {
  data?: {
    path?: string;
  };
}

const ALLOWED_VIDEO_FORMATS = ['mp4'];
// const MAX_VIDEO_SIZE_IN_MB = 1;

export const CheckPointInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const [errorMessage, setErrorMessage] = useState('');

  const translatedCheckPoint = getTranslatedItem(data?.translatedCheckPoints);

  /**
   * === Set Value Watchers section ===
   */
  const nameValue = watch && watch('name' as Path<TSchema>);

  const reportIconValue =
    watch && watch('reportIcon' as Path<TSchema>, data?.reportIcon ?? '');

  const appIconValue =
    watch && watch('appIcon' as Path<TSchema>, data?.appIcon ?? '');

  const typeValue = watch && watch('type' as Path<TSchema>, data?.type ?? '');

  const enableLicensePlateDetectionValue =
    watch &&
    watch(
      'enableLicensePlateDetection' as Path<TSchema>,
      data?.enableLicensePlateDetection ?? false,
    );

  const enablePlateCorrectionValue =
    watch &&
    watch(
      'enablePlateCorrection' as Path<TSchema>,
      data?.enablePlateCorrection ?? false,
    );

  const enableVINDetectionValue =
    watch &&
    watch(
      'enableVINDetection' as Path<TSchema>,
      data?.enableVINDetection ?? false,
    );

  const enableVINCorrectionValue =
    watch &&
    watch(
      'enableVINCorrection' as Path<TSchema>,
      data?.enableVINCorrection ?? false,
    );

  const canBeSkippedValue =
    watch &&
    watch('canBeSkipped' as Path<TSchema>, data?.canBeSkipped ?? false);

  const enableRegistrationCertificateValue =
    watch &&
    watch(
      'enableRegistrationCertificate' as Path<TSchema>,
      data?.enableRegistrationCertificate ?? false,
    );

  const checkDistanceValue =
    watch &&
    watch('checkDistance' as Path<TSchema>, data?.checkDistance ?? false);

  const enableSoundValue =
    watch && watch('enableSound' as Path<TSchema>, data?.enableSound ?? false);

  const activeValue = watch && watch('active' as Path<TSchema>);
  /**
   * === END OF Set Value Watchers section ===
   */

  const isVideoCheckPoint = typeValue === CheckPointType.VIDEO;

  const { data: matchingCheckPointError } = useGetMatchingCheckPointError({
    id: data?.id,
    name: nameValue,
    type: typeValue,
  });

  const { mutate: uploadVideo, isPending: isPendingUploadVideo } =
    useUploadCheckPointVideo({
      handleSuccess: (data: UploadedVideoData) => {
        setValue(
          'appVideo' as Path<TSchema>,
          (data?.data?.path ?? '') as PathValue<TSchema, Path<TSchema>>,
        );
      },
    });

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (nameValue) {
      resetFieldError<TSchema>({ fieldName: 'name', setError });
    }

    if (reportIconValue) {
      resetFieldError<TSchema>({ fieldName: 'reportIcon', setError });
    }

    if (appIconValue) {
      resetFieldError<TSchema>({ fieldName: 'appIcon', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue, reportIconValue, appIconValue]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setCheckPointRequiredFields({
        setValue,
        data,
        translatedItem: translatedCheckPoint,
      });
      setValue('appSketch' as Path<TSchema>, data.appSketch ?? '');
      setValue('appVideo' as Path<TSchema>, data.appVideo ?? '');
      setValue(
        'enableLicensePlateDetection' as Path<TSchema>,
        data.enableLicensePlateDetection ?? false,
      );
      setValue(
        'enablePlateCorrection' as Path<TSchema>,
        data.enablePlateCorrection ?? false,
      );
      setValue(
        'enableVINDetection' as Path<TSchema>,
        data.enableVINDetection ?? false,
      );
      setValue(
        'enableVINCorrection' as Path<TSchema>,
        data.enableVINCorrection ?? false,
      );
      setValue('canBeSkipped' as Path<TSchema>, data.canBeSkipped ?? false);
      setValue(
        'enableRegistrationCertificate' as Path<TSchema>,
        data.enableRegistrationCertificate ?? false,
      );
      setValue(
        'description' as Path<TSchema>,
        (translatedCheckPoint?.description || data.description) ?? '',
      );
      setValue(
        'outOfServiceDescription' as Path<TSchema>,
        (translatedCheckPoint?.outOfServiceDescription ||
          data.outOfServiceDescription) ??
          '',
      );
      setValue('checkDistance' as Path<TSchema>, data.checkDistance ?? false);
      setValue('enableSound' as Path<TSchema>, data.enableSound ?? false);
      setValue('active' as Path<TSchema>, data.active);
      setValue('isFrontPart' as Path<TSchema>, data.isFrontPart ?? false);
      setValue('isBackPart' as Path<TSchema>, data.isBackPart ?? false);
      setValue('isLeftPart' as Path<TSchema>, data.isLeftPart ?? false);
      setValue('isRightPart' as Path<TSchema>, data.isRightPart ?? false);
    }
  }, [setValue, data, translatedCheckPoint]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === License Plate and VIN corrections section ===
   */
  useEffect(() => {
    if (!enableLicensePlateDetectionValue) {
      setValue(
        'enablePlateCorrection' as Path<TSchema>,
        false as PathValue<TSchema, Path<TSchema>>,
      );
    }
  }, [enableLicensePlateDetectionValue, setValue]);

  useEffect(() => {
    if (!enableVINDetectionValue) {
      setValue(
        'enableVINCorrection' as Path<TSchema>,
        false as PathValue<TSchema, Path<TSchema>>,
      );
    }
  }, [enableVINDetectionValue, setValue]);
  /**
   * === END OF License Plate and VIN corrections section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const checkPointErrorMessages =
      matchingCheckPointError?.data?.errorMessages;

    if (checkPointErrorMessages?.includes(FieldError.NAME_WITH_TYPE_EXISTS)) {
      setError('name' as Path<TSchema>, {
        message: t('checkPointNameAlreadyExistsWithThisType'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card
          title={
            <TitleSection
              text={t('theCheckPoint')}
              defaultIconClassName="ki-badge"
            />
          }
          footer={
            <Button
              type="submit"
              buttonClassName="px-8"
              text={
                submitDisabled ? `${t('common:loading')}...` : t('common:save')
              }
              disabled={submitDisabled || isPendingUploadVideo}
            />
          }
          bodyClassName="py-8 flex flex-col gap-5"
        >
          <FormField
            text={t('common:icons')}
            containerClassName="mb-2 max-md:mb-0"
            textWrapperClassName="mt-5.5 max-md:mt-0"
            subTextClassName="text-gray-600"
          >
            <div className="-mt-2 flex flex-wrap flex-1 gap-5">
              <div className="w-[230px] flex flex-col gap-1.5 max-md:gap-0 max-md:w-full max-md:items-center">
                <div>
                  <div className="h-[35px] flex items-end text-gray-5B5B5B text-2sm font-semibold mb-[6px] max-md:h-auto max-md:text-center">
                    {`${t('reportIcon')} (PNG)`}
                    <span className="ml-0.75 text-red-FF2929 text-sm">
                      *
                    </span>{' '}
                    <span className="ml-0.75 text-gray-5B5B5B text-2sm font-semibold">
                      :
                    </span>
                  </div>
                  <Avatar
                    outerContainerClassName="max-md:w-full"
                    containerClassName="max-md:mx-auto"
                    allowedFormats={['png']}
                    onShowErrorMessage={(message) => {
                      setErrorMessage(message);
                      showModal('#checkpoint-image-error-modal');
                    }}
                    showAuthorizedFormatInfo={false}
                    imageUrl={data?.reportIcon}
                    onChange={(url) =>
                      setValue?.(
                        'reportIcon' as Path<TSchema>,
                        url as PathValue<TSchema, Path<TSchema>>,
                      )
                    }
                  />
                </div>
                {errors && (
                  <ErrorMessage
                    message={errors && (errors.reportIcon?.message as string)}
                  />
                )}
              </div>
              <div className="w-[230px] flex flex-col gap-1.5 max-md:gap-0 max-md:w-full max-md:items-center">
                <div>
                  <div className="h-[35px] flex items-end text-gray-5B5B5B text-2sm font-semibold mb-[6px] max-md:h-auto max-md:text-center">
                    {`${t('appIcon')} (SVG)`}
                    <span className="ml-0.75 text-red-FF2929 text-sm">
                      *
                    </span>{' '}
                    <span className="ml-0.75 text-gray-5B5B5B text-2sm font-semibold">
                      :
                    </span>
                  </div>
                  <Avatar
                    outerContainerClassName="max-md:w-full"
                    containerClassName="max-md:mx-auto"
                    allowedFormats={['svg']}
                    onShowErrorMessage={(message) => {
                      setErrorMessage(message);
                      showModal('#checkpoint-image-error-modal');
                    }}
                    showAuthorizedFormatInfo={false}
                    imageUrl={data?.appIcon}
                    onChange={(url) =>
                      setValue?.(
                        'appIcon' as Path<TSchema>,
                        url as PathValue<TSchema, Path<TSchema>>,
                      )
                    }
                  />
                </div>
                {errors && (
                  <ErrorMessage
                    message={errors && (errors.appIcon?.message as string)}
                  />
                )}
              </div>
              <div className="max-w-[260px] flex flex-col max-md:max-w-none max-md:gap-0 max-md:w-full max-md:items-center">
                <div>
                  <div className="h-[35px] flex items-end text-gray-5B5B5B text-2sm font-semibold mb-[6px] max-md:h-auto max-md:text-center">
                    {t('appMask')} <br />{' '}
                    {`(${['SVG', 'JSON'].join(
                      ` ${t('common:or').toLowerCase()} `,
                    )})`}{' '}
                    :
                  </div>
                  <Avatar
                    outerContainerClassName="max-md:w-full"
                    containerClassName="max-md:mx-auto"
                    allowedFormats={['svg', 'json']}
                    onShowErrorMessage={(message) => {
                      setErrorMessage(message);
                      showModal('#checkpoint-image-error-modal');
                    }}
                    showAuthorizedFormatInfo={false}
                    imageUrl={data?.appSketch}
                    onChange={(url) => {
                      // Handle both image URLs and JSON objects
                      const value =
                        typeof url === 'object' ? JSON.stringify(url) : url;
                      setValue?.(
                        'appSketch' as Path<TSchema>,
                        value as PathValue<TSchema, Path<TSchema>>,
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </FormField>
          <FormField
            text={t('common:checkPointName')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('name' as Path<TSchema>))}
                errorMessage={errors && (errors.name?.message as string)}
                placeholder={t('common:checkPointName')}
                type="text"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:type')}
            required
            textWrapperClassName="mt-2.5"
          >
            <div className="flex flex-1 flex-wrap items-start h-0 gap-5 mt-2.5">
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Radio
                    {...field}
                    errorMessage={errors && (errors.type?.message as string)}
                    radioListClassName="flex-row gap-6"
                    defaultValue={data?.type}
                    items={[
                      {
                        displayedText: t('common:video'),
                        value: CheckPointType.VIDEO,
                      },
                      {
                        displayedText: t('common:photo'),
                        value: CheckPointType.PHOTO,
                      },
                    ]}
                  />
                )}
              />
            </div>
          </FormField>
          <FormField
            containerClassName="mt-5.5"
            textOuterWrapperClassName="mt-0.5"
            text={t('common:vehiclePart')}
          >
            <div className="flex flex-col gap-2.5">
              <Checkbox
                {...(register && register('isFrontPart' as Path<TSchema>))}
                text={t('vehicule:partFront')}
              />
              <Checkbox
                {...(register && register('isBackPart' as Path<TSchema>))}
                text={t('vehicule:partBack')}
              />
              <Checkbox
                {...(register && register('isLeftPart' as Path<TSchema>))}
                text={t('vehicule:partLeft')}
              />
              <Checkbox
                {...(register && register('isRightPart' as Path<TSchema>))}
                text={t('vehicule:partRight')}
              />
            </div>
          </FormField>
          <FormField containerClassName="mt-6" text={t('common:licensePlate')}>
            <div className="flex flex-col gap-2.5">
              <Switch
                {...(register &&
                  register('enableLicensePlateDetection' as Path<TSchema>))}
                errorMessage={
                  errors &&
                  (errors.enableLicensePlateDetection?.message as string)
                }
                checked={enableLicensePlateDetectionValue}
                rightTextClassName="text-gray-700"
                rightText={
                  <Trans
                    i18nKey={t('enableLicensePlateDetection')}
                    components={{
                      span: <span className="underline" />,
                    }}
                  />
                }
              />
              {enableLicensePlateDetectionValue && (
                <Switch
                  {...(register &&
                    register('enablePlateCorrection' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.enablePlateCorrection?.message as string)
                  }
                  checked={enablePlateCorrectionValue}
                  rightTextClassName="text-gray-700"
                  rightText={
                    <Trans
                      i18nKey={t('allowManualCorrectionOfLicensePlateNumber')}
                      components={{
                        span: <span className="underline" />,
                      }}
                    />
                  }
                />
              )}
            </div>
          </FormField>
          <FormField containerClassName="mt-6" text={t('common:VIN')}>
            <div className="flex flex-col gap-2.5">
              <Switch
                {...(register &&
                  register('enableVINDetection' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.enableVINDetection?.message as string)
                }
                checked={enableVINDetectionValue}
                rightTextClassName="text-gray-700"
                rightText={
                  <Trans
                    i18nKey={t('enableVINDetection')}
                    components={{
                      span: <span className="underline" />,
                    }}
                  />
                }
              />
              {enableVINDetectionValue && (
                <Switch
                  {...(register &&
                    register('enableVINCorrection' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.enableVINCorrection?.message as string)
                  }
                  checked={enableVINCorrectionValue}
                  rightTextClassName="text-gray-700"
                  rightText={
                    <Trans
                      i18nKey={t('allowManualCorrectionOfVIN')}
                      components={{
                        span: <span className="underline" />,
                      }}
                    />
                  }
                />
              )}
            </div>
          </FormField>
          <FormField
            containerClassName="mt-6"
            text={t('thisCheckPointConcernsVehicleRegistrationCertificate')}
          >
            <div className="flex flex-col gap-2.5">
              <Switch
                {...(register &&
                  register('enableRegistrationCertificate' as Path<TSchema>))}
                errorMessage={
                  errors &&
                  (errors.enableRegistrationCertificate?.message as string)
                }
                checked={enableRegistrationCertificateValue}
              />
            </div>
          </FormField>
          <FormField
            containerClassName="mt-6"
            text={t('thisCheckPointCanBeSkipped')}
          >
            <div className="flex flex-col gap-2.5">
              <Switch
                {...(register && register('canBeSkipped' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.canBeSkipped?.message as string)
                }
                checked={canBeSkippedValue}
              />
            </div>
          </FormField>
          <FormField text={t('common:description')} textWrapperClassName="mt-5">
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
              <Textarea
                {...(register && register('description' as Path<TSchema>))}
                errorMessage={errors && (errors.description?.message as string)}
                placeholder={t('common:description')}
              />
            </div>
          </FormField>
          <FormField
            text={t('outOfServiceDescription')}
            textWrapperClassName="mt-5"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
              <Textarea
                {...(register &&
                  register('outOfServiceDescription' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.outOfServiceDescription?.message as string)
                }
                placeholder={t('outOfServiceDescription')}
              />
            </div>
          </FormField>
          {isVideoCheckPoint && (
            <>
              <FormField
                text={`${t('moveCloser')} / ${t('stepBackALittle')}`}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
                  <Switch
                    {...(register &&
                      register('checkDistance' as Path<TSchema>))}
                    errorMessage={
                      errors && (errors.checkDistance?.message as string)
                    }
                    checked={checkDistanceValue}
                  />
                </div>
              </FormField>
              <FormField
                text={t('common:enableSound')}
                subText={t('functionalityOnlyAvailableForAppProClient')}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
                  <Switch
                    {...(register && register('enableSound' as Path<TSchema>))}
                    errorMessage={
                      errors && (errors.enableSound?.message as string)
                    }
                    checked={enableSoundValue}
                  />
                </div>
              </FormField>
            </>
          )}
          <FormField
            text={t('helpVideo')}
            subText={
              <>
                <div>
                  {t('common:allowedFormat')} :{' '}
                  {ALLOWED_VIDEO_FORMATS.join(', ').toUpperCase()}
                </div>
                {/* <div>
                    {t('common:maxWeight')} {MAX_VIDEO_SIZE_IN_MB}{' '}
                    {t('common:mb')}
                  </div> */}
              </>
            }
            textWrapperClassName="mt-6"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-6">
              <VideoUpload
                t={t}
                initialVideo={
                  data?.appVideo && `${R2_PUBLIC_DOMAIN}/${data?.appVideo}`
                }
                allowedFormats={ALLOWED_VIDEO_FORMATS}
                // maximumVideoSize={1}
                disabledChooseAnotherVideoButton={isPendingUploadVideo}
                mutateUploadVideo={uploadVideo}
              />
            </div>
          </FormField>
          <FormField
            text={t('common:activeStatus')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
              <Switch
                {...(register && register('active' as Path<TSchema>))}
                errorMessage={errors && (errors.active?.message as string)}
                checked={data ? activeValue : true}
              />
            </div>
          </FormField>
        </Card>
      </form>
      <Modal
        id="checkpoint-image-error-modal"
        title={t('common:imageIsIncorrect')}
        centerY
        size="small"
        showCancelBtn={false}
        showSecondBtn
        secondBtnLabel={t('common:ok')}
      >
        <p>{errorMessage}</p>
      </Modal>
      {isPendingUploadVideo && <Loader />}
    </>
  );
};
